function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { useCallback } from "react";
import generateRandomKey from "@sprout/client-lib/dist/generateRandomKey";
import { useParams } from "react-router-dom";
import { TopicPage_ViewsTopic_SectionFragmentDoc, useTopicPage_SortTopicListMutation, ItemType, TopicPage_Views_TopicDocument } from "sprout-graphql";
import update from "immutability-helper";
import reorder from "sprout-lib/dist/reorder";
import { useSnackbar } from "sprout-ui-dom/dist/Snackbar";
function getNewIndex(nodes, variables) {
    const afterIndex = variables.afterItemId ? nodes.findIndex(({ id  })=>id === variables.afterItemId) : variables.afterLastItem ? nodes.length - 1 : -1;
    return afterIndex + 1;
}
function getNewNodesWithInsertedPost(nodes, variables, postToInsert) {
    /* It is possible that the `Post` to insert is already returned
   * by `optimisticResponse`, so we need to filter it to prevent
   * duplicated item
   */ const expectedNodes = nodes.filter((post)=>post.id !== postToInsert.id);
    const newIndex = getNewIndex(expectedNodes, variables);
    return [
        ...expectedNodes.slice(0, newIndex),
        postToInsert,
        ...expectedNodes.slice(newIndex), 
    ];
}
var SortTopicListScenarios;
(function(SortTopicListScenarios) {
    SortTopicListScenarios[SortTopicListScenarios["DEFAULT"] = 0] = "DEFAULT";
    SortTopicListScenarios[SortTopicListScenarios["SECTION_COMBINE"] = 1] = "SECTION_COMBINE";
    SortTopicListScenarios[SortTopicListScenarios["POST_L2_L2_SAME_SECTION"] = 2] = "POST_L2_L2_SAME_SECTION";
    SortTopicListScenarios[SortTopicListScenarios["POST_L2_L2_DIFFERENT_SECTION"] = 3] = "POST_L2_L2_DIFFERENT_SECTION";
    SortTopicListScenarios[SortTopicListScenarios["POST_L2_TO_L1"] = 4] = "POST_L2_TO_L1";
    SortTopicListScenarios[SortTopicListScenarios["POST_L1_TO_L2"] = 5] = "POST_L1_TO_L2";
})(SortTopicListScenarios || (SortTopicListScenarios = {}));
const nextDirectPostCount = (directPostCount, condition)=>{
    if (directPostCount) {
        if (condition === "increment") return directPostCount + 1;
        return directPostCount - 1;
    }
    return directPostCount;
};
function useSortTopicList() {
    const { topicId  } = useParams();
    const [sortTopicList] = useTopicPage_SortTopicListMutation();
    const showSnackbar = useSnackbar();
    return useCallback(({ variables , optimisticResponseData  })=>{
        const { type , scenario , from , to , afterItem  } = optimisticResponseData;
        const position = afterItem && afterItem.position != null ? afterItem.position + Number.MIN_VALUE : -Number.MAX_SAFE_INTEGER;
        sortTopicList({
            variables,
            context: {
                queue: true
            },
            optimisticResponse: {
                moveItemsByIdIntoTopic: {
                    items: [
                        {
                            id: variables.itemId,
                            nodeId: generateRandomKey(),
                            type,
                            position,
                            __typename: "Item"
                        }, 
                    ],
                    __typename: "MoveItemsByIdIntoTopicPayload"
                }
            },
            update (proxy) {
                const options = {
                    query: TopicPage_Views_TopicDocument,
                    variables: {
                        topicId: Number(topicId)
                    }
                };
                const cache = proxy.readQuery(options);
                if (scenario === SortTopicListScenarios.SECTION_COMBINE && type === ItemType.Topic) proxy.writeQuery(_objectSpread({}, options, {
                    data: update(cache, {
                        topic: {
                            sections: {
                                totalCount: {
                                    $apply: (totalCount)=>totalCount - 1
                                },
                                nodes: {
                                    $apply: (nodes)=>nodes.filter((section)=>section.id !== variables.itemId)
                                }
                            }
                        }
                    })
                }));
                if (scenario === SortTopicListScenarios.DEFAULT) proxy.writeQuery(_objectSpread({}, options, {
                    data: update(cache, {
                        topic: {
                            [type === ItemType.Topic ? "sections" : "posts"]: {
                                nodes: {
                                    $apply: (nodes)=>{
                                        const oldIndex = nodes.findIndex(({ id  })=>id === variables.itemId);
                                        const newIndex = getNewIndex(nodes, variables);
                                        return reorder(nodes, oldIndex, newIndex);
                                    }
                                }
                            }
                        }
                    })
                }));
                if (type === ItemType.Post && from && to) {
                    if (scenario === SortTopicListScenarios.POST_L2_L2_SAME_SECTION) {
                        const fromOptions = {
                            id: from,
                            fragment: TopicPage_ViewsTopic_SectionFragmentDoc,
                            fragmentName: "TopicPage_ViewsTopic_Section"
                        };
                        const fromCache = proxy.readFragment(fromOptions);
                        proxy.writeFragment(_objectSpread({}, fromOptions, {
                            data: update(fromCache, {
                                posts: {
                                    nodes: {
                                        $apply: (nodes)=>{
                                            const oldIndex = nodes.findIndex(({ id  })=>id === variables.itemId);
                                            const newIndex = getNewIndex(nodes, variables);
                                            return reorder(nodes, oldIndex, newIndex);
                                        }
                                    }
                                }
                            })
                        }));
                    }
                    if ([
                        SortTopicListScenarios.POST_L2_L2_DIFFERENT_SECTION,
                        SortTopicListScenarios.POST_L2_TO_L1,
                        SortTopicListScenarios.POST_L1_TO_L2, 
                    ].includes(scenario)) {
                        if ([
                            SortTopicListScenarios.POST_L2_L2_DIFFERENT_SECTION,
                            SortTopicListScenarios.POST_L2_TO_L1, 
                        ].includes(scenario)) {
                            // remove
                            const fromOptions = {
                                id: from,
                                fragment: TopicPage_ViewsTopic_SectionFragmentDoc,
                                fragmentName: "TopicPage_ViewsTopic_Section"
                            };
                            const fromCache = proxy.readFragment(fromOptions);
                            const post = fromCache === null || fromCache === void 0 ? void 0 : fromCache.posts.nodes.find(({ id  })=>id === variables.itemId);
                            proxy.writeFragment(_objectSpread({}, fromOptions, {
                                data: update(fromCache, {
                                    directPostCount: {
                                        $apply: (directPostCount)=>nextDirectPostCount(directPostCount, "decrement")
                                    },
                                    posts: {
                                        totalCount: {
                                            $apply: (totalCount)=>totalCount - 1
                                        },
                                        nodes: {
                                            $apply: (nodes)=>{
                                                return nodes.filter(({ id  })=>id !== variables.itemId);
                                            }
                                        }
                                    }
                                })
                            }));
                            // add
                            if (post) {
                                const postWithNewParentTopicId = update(post, {
                                    parentTopicId: {
                                        $set: variables.parentTopicId
                                    }
                                });
                                if (scenario === SortTopicListScenarios.POST_L2_L2_DIFFERENT_SECTION) {
                                    const toOptions = {
                                        id: to,
                                        fragment: TopicPage_ViewsTopic_SectionFragmentDoc,
                                        fragmentName: "TopicPage_ViewsTopic_Section"
                                    };
                                    const toCache = proxy.readFragment(toOptions);
                                    proxy.writeFragment(_objectSpread({}, toOptions, {
                                        data: update(toCache, {
                                            directPostCount: {
                                                $apply: (directPostCount)=>nextDirectPostCount(directPostCount, "increment")
                                            },
                                            posts: {
                                                totalCount: {
                                                    $apply: (totalCount)=>totalCount + 1
                                                },
                                                nodes: {
                                                    $apply: (nodes)=>{
                                                        return getNewNodesWithInsertedPost(nodes, variables, postWithNewParentTopicId);
                                                    }
                                                }
                                            }
                                        })
                                    }));
                                }
                                if (scenario === SortTopicListScenarios.POST_L2_TO_L1) proxy.writeQuery(_objectSpread({}, options, {
                                    data: update(cache, {
                                        topic: {
                                            posts: {
                                                totalCount: {
                                                    $apply: (totalCount)=>totalCount + 1
                                                },
                                                nodes: {
                                                    $apply: (nodes)=>{
                                                        return getNewNodesWithInsertedPost(nodes, variables, postWithNewParentTopicId);
                                                    }
                                                }
                                            }
                                        }
                                    })
                                }));
                            }
                        }
                        //
                        if (scenario === SortTopicListScenarios.POST_L1_TO_L2) {
                            var ref;
                            // remove
                            proxy.writeQuery(_objectSpread({}, options, {
                                data: update(cache, {
                                    topic: {
                                        posts: {
                                            totalCount: {
                                                $apply: (totalCount)=>totalCount - 1
                                            },
                                            nodes: {
                                                $apply: (nodes)=>nodes.filter((post)=>post.id !== variables.itemId)
                                            }
                                        }
                                    }
                                })
                            }));
                            const post1 = cache === null || cache === void 0 ? void 0 : (ref = cache.topic) === null || ref === void 0 ? void 0 : ref.posts.nodes.find(({ id  })=>id === variables.itemId);
                            // add
                            if (post1) {
                                const postWithNewParentTopicId = update(post1, {
                                    parentTopicId: {
                                        $set: variables.parentTopicId
                                    }
                                });
                                const toOptions = {
                                    id: to,
                                    fragment: TopicPage_ViewsTopic_SectionFragmentDoc,
                                    fragmentName: "TopicPage_ViewsTopic_Section"
                                };
                                const toCache = proxy.readFragment(toOptions);
                                proxy.writeFragment(_objectSpread({}, toOptions, {
                                    data: update(toCache, {
                                        directPostCount: {
                                            $apply: (directPostCount)=>nextDirectPostCount(directPostCount, "increment")
                                        },
                                        posts: {
                                            totalCount: {
                                                $apply: (totalCount)=>totalCount + 1
                                            },
                                            nodes: {
                                                $apply: (nodes)=>{
                                                    return getNewNodesWithInsertedPost(nodes, variables, postWithNewParentTopicId);
                                                }
                                            }
                                        }
                                    })
                                }));
                            }
                        }
                    }
                }
            }
        }).catch(()=>{
            showSnackbar.error();
        });
    }, [
        showSnackbar,
        sortTopicList,
        topicId
    ]);
}
function useHandleDragEnd({ posts , sections  }) {
    const { topicId  } = useParams();
    const currentTopicId = Number(topicId);
    const sortTopicList = useSortTopicList();
    return useCallback((result)=>{
        const { type , draggableId , source , destination , combine  } = result;
        const itemId = Number(draggableId);
        /**
       * We only support combining branches at this moment
       *
       * e.g. https://github.com/atlassian/react-beautiful-dnd/blob/ec06fa266e1617cab2402e0613b36d88b9547f7f/stories/src/vertical/quote-app.jsx#L38
       */ if (combine && type === ItemType.Topic) sortTopicList({
            variables: {
                itemId,
                afterItemId: null,
                parentTopicId: Number(combine.draggableId)
            },
            optimisticResponseData: {
                type,
                scenario: SortTopicListScenarios.SECTION_COMBINE
            }
        });
        /**
       * don't sort if the item is dropped outside the droppable
       * don't sort if the item is touched but not moved
       */ if (!destination || destination.index === source.index && destination.droppableId === source.droppableId) return;
        /**
       * There's a possibility that `targetItem` is undefined, due to some unknown
       * bug.
       * https://github.com/microsoft/TypeScript/issues/13778
       */ const from = JSON.parse(source.droppableId);
        const to = JSON.parse(destination.droppableId);
        if (destination.droppableId === source.droppableId && to.id === currentTopicId) {
            const items = type === ItemType.Topic ? sections : posts;
            const afterItem = destination.index < source.index ? items[destination.index - 1] : items[destination.index];
            sortTopicList({
                variables: {
                    parentTopicId: currentTopicId,
                    itemId,
                    afterItemId: (afterItem === null || afterItem === void 0 ? void 0 : afterItem.id) || null
                },
                optimisticResponseData: {
                    type: type === ItemType.Topic ? ItemType.Topic : ItemType.Post,
                    scenario: SortTopicListScenarios.DEFAULT,
                    afterItem
                }
            });
        }
        if (type === ItemType.Post) {
            // NESTED_POST_SAME_SECTION
            if (from.id === to.id) {
                const section = sections.find(({ id  })=>id === to.id);
                const items = section === null || section === void 0 ? void 0 : section.posts.nodes;
                if (items) {
                    const afterItem = destination.index < source.index ? items[destination.index - 1] : items[destination.index];
                    sortTopicList({
                        variables: {
                            parentTopicId: to.id,
                            itemId,
                            afterItemId: (afterItem === null || afterItem === void 0 ? void 0 : afterItem.id) || null
                        },
                        optimisticResponseData: {
                            scenario: SortTopicListScenarios.POST_L2_L2_SAME_SECTION,
                            type: ItemType.Post,
                            afterItem,
                            from: from.nodeId,
                            to: to.nodeId
                        }
                    });
                }
            }
            if (from.id !== to.id) {
                let postsToFind = [];
                let scenario;
                if (to.id !== currentTopicId && from.id !== currentTopicId) {
                    scenario = SortTopicListScenarios.POST_L2_L2_DIFFERENT_SECTION;
                    const section = sections.find(({ id  })=>id === to.id);
                    postsToFind = (section === null || section === void 0 ? void 0 : section.posts.nodes) || [];
                } else if (to.id === currentTopicId) {
                    scenario = SortTopicListScenarios.POST_L2_TO_L1;
                    postsToFind = posts;
                } else {
                    scenario = SortTopicListScenarios.POST_L1_TO_L2;
                    const section = sections.find(({ id  })=>id === to.id);
                    postsToFind = (section === null || section === void 0 ? void 0 : section.posts.nodes) || [];
                }
                const afterItem = postsToFind[destination.index - 1];
                const afterItemId = (afterItem === null || afterItem === void 0 ? void 0 : afterItem.id) || null;
                sortTopicList({
                    variables: {
                        itemId,
                        afterItemId,
                        parentTopicId: to.id
                    },
                    optimisticResponseData: {
                        scenario,
                        type: ItemType.Post,
                        afterItem,
                        from: from.nodeId,
                        to: to.nodeId
                    }
                });
            }
        }
    }, [
        currentTopicId,
        posts,
        sections,
        sortTopicList
    ]);
}
export default useHandleDragEnd;
